import translate from '@translate';
import React, { FC, useEffect, useState, useRef, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ApplicationStatusMessageType } from 'src/types/applicationStatusMessage';
import { checkFormValidity } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import { useNavigate } from 'react-router-dom';
import TopBlock from './TopBlock';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import {
    clearApplicationStatusMessageDetail,
    createApplicationStatusMessageDetailFetch,
    getApplicationStatusMessageDetailFetch,
    updateApplicationStatusMessageDetailFetch,
} from 'src/resources/applicationStatusMessageDetail/reducer';
import { FormErrorsType, OnChangeType } from '../../types/global';
import { TableColumnType, TableRowType } from '../../types/table';
import { ConstantsCodesObjectType } from '../../types/constantsCodes';
import getStatusColor from '../../utils/getStatusColor';

export type EditKeysType = keyof Omit<ApplicationStatusMessageType, 'ID' | 'ACTIVE' | 'CREATED_AT'>;

const initData = {};
const ApplicationStatusMessageDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idApplicationStatusMessage } = useParams();

    const newApplicationStatusMessage = !idApplicationStatusMessage;

    const formRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.applicationStatusMessageDetail.data);
    const loading = useSelector((state: RootStoreType) => state.applicationStatusMessageDetail.loading);
    const applicationMessageCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.applicationMessageCodes
    );
    const applicationMessageNotificationTypesCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.applicationMessageNotificationTypesCodes
    );

    const [editData, setEditData] = useState<ApplicationStatusMessageType>({});
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionCreate = useCheckPermission('APPLICATION_STATUS_MESSAGE.CREATE');
    const permissionUpdate = useCheckPermission('APPLICATION_STATUS_MESSAGE.UPDATE');
    const disableEdit = newApplicationStatusMessage ? !permissionCreate : !permissionUpdate;
    const permissionDelete = useCheckPermission('APPLICATION_STATUS_MESSAGE.DELETE');
    const disableDelete = newApplicationStatusMessage || !permissionDelete;

    const backUrl = '/admin/application_status_messages/';

    useEffect(() => {
        if (idApplicationStatusMessage) {
            dispatch(
                setLocationParams({
                    name: translate('applicationStatusMessageDetail.title', { idApplicationStatusMessage }),
                    backUrl,
                })
            );
        } else {
            dispatch(setLocationParams({ name: translate('applicationStatusMessageDetail.titleNew'), backUrl }));
        }
    }, [idApplicationStatusMessage]);

    useEffect(() => {
        if (idApplicationStatusMessage) {
            dispatch(getApplicationStatusMessageDetailFetch(idApplicationStatusMessage));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearApplicationStatusMessageDetail());
        };
    }, [idApplicationStatusMessage]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }

        if (!idApplicationStatusMessage && data?.ID) {
            navigate(`/admin/application_status_messages/${String(data.ID)}/`);
        }
    }, [data]);

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(
                    newApplicationStatusMessage
                        ? 'applicationStatusMessageDetail.create'
                        : 'applicationStatusMessageDetail.save'
                ),
                content: translate(
                    newApplicationStatusMessage
                        ? 'applicationStatusMessageDetail.createConfirm'
                        : 'applicationStatusMessageDetail.saveConfirm'
                ),
                action: () => {
                    const formatData = { ...editData };

                    if (newApplicationStatusMessage) {
                        dispatch(createApplicationStatusMessageDetailFetch(formatData));
                    } else {
                        dispatch(
                            updateApplicationStatusMessageDetailFetch({ idApplicationStatusMessage, data: formatData })
                        );
                    }
                },
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    const handleToggleActivateProduct = () => {
        setConfirmParams({
            title: translate(
                !data?.ACTIVE ? 'applicationStatusMessageDetail.activate' : 'applicationStatusMessageDetail.deactivate'
            ),
            content: translate(
                !data?.ACTIVE
                    ? 'applicationStatusMessageDetail.activateConfirm'
                    : 'applicationStatusMessageDetail.deactivateConfirm'
            ),
            action: () => {
                dispatch(
                    updateApplicationStatusMessageDetailFetch({
                        idApplicationStatusMessage,
                        data: { ...data, ACTIVE: data?.ACTIVE ? 0 : 1 },
                    })
                );
            },
        });
    };

    return (
        <>
            <TopBlock
                data={data}
                idApplicationStatusMessage={idApplicationStatusMessage}
                newApplicationStatusMessage={newApplicationStatusMessage}
                disableEdit={disableEdit}
                disableDelete={disableDelete}
                handleSave={handleSave}
                toggleActivateProduct={handleToggleActivateProduct}
            />
            <form ref={formRef}>
                {loading && <Loading />}

                <FormBlock>
                    <Detail
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        disableEdit={disableEdit}
                        applicationMessageCodes={applicationMessageCodes}
                        applicationMessageNotificationTypesCodes={applicationMessageNotificationTypesCodes}
                    />
                </FormBlock>
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    action={confirmParams.action}
                    onClose={(): any => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default ApplicationStatusMessageDetailContainer;
